import React, { Fragment } from 'react';
import { KeenIcon } from '@/components';
import { useDataGrid } from '..';
import { RowData, Table, PaginationState } from '@tanstack/react-table';

const DataGridPagination = () => {
  const { table, totalRows, props } = useDataGrid();
  const typedTable = table as Table<RowData>;
  // Calculate pagination values
  const pageIndex = typedTable.getState().pagination.pageIndex;
  const pageSize = typedTable.getState().pagination.pageSize;

  // Calculate from, to, and total count values for pagination info
  const from = pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, totalRows);

  // Replace placeholders in paginationInfo
  const paginationInfo = props.pagination?.info
    ? props.pagination.info
        .replace('{from}', from.toString())
        .replace('{to}', to.toString())
        .replace('{count}', totalRows.toString())
    : `${from} - ${to} of ${totalRows}`; // Default fallback format

  // Pagination limit logic
  const pageCount = props.pagination?.totalPages || typedTable.getPageCount();
  const paginationMoreLimit = props.pagination?.moreLimit || 5;

  // Determine the start and end of the pagination group
  const currentGroupStart =
    Math.floor(pageIndex / paginationMoreLimit) * paginationMoreLimit;
  const currentGroupEnd = Math.min(
    currentGroupStart + paginationMoreLimit,
    pageCount
  );

  // Function to handle page changes with support for both internal and external pagination
  const handlePageChange = (newPageIndex: number) => {
    if (props.pagination?.setPagination) {
      // Use the parent component's setPagination function for server-side pagination
      props.pagination.setPagination((prev: PaginationState) => ({
        ...prev,
        pageIndex: newPageIndex,
      }));
    } else {
      // Use the table's built-in pagination for client-side pagination
      typedTable.setPageIndex(newPageIndex);
    }
  };

  // Render page buttons based on the current group
  const renderPageButtons = () => {
    const buttons = [];
    for (let i = currentGroupStart; i < currentGroupEnd; i++) {
      buttons.push(
        <button
          key={i}
          className={`btn ${pageIndex === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </button>
      );
    }
    return buttons;
  };

  // Render a "previous" ellipsis button if there are previous pages to show
  const renderEllipsisPrevButton = () => {
    if (currentGroupStart > 0) {
      return (
        <button
          className="btn"
          onClick={() => handlePageChange(currentGroupStart - 1)}
        >
          ...
        </button>
      );
    }
    return null; // No ellipsis needed if we're in the first group
  };

  // Render a "next" ellipsis button if there are more pages to show after the current group
  const renderEllipsisNextButton = () => {
    if (currentGroupEnd < pageCount) {
      return (
        <button
          className="btn"
          onClick={() => handlePageChange(currentGroupEnd)}
        >
          ...
        </button>
      );
    }
    return null; // No ellipsis needed if we're in the last group
  };

  // Function to handle previous page navigation
  const handlePreviousPage = () => {
    if (pageIndex > 0) {
      if (props.pagination?.setPagination) {
        props.pagination.setPagination((prev: PaginationState) => ({
          ...prev,
          pageIndex: prev.pageIndex - 1,
        }));
      } else {
        typedTable.previousPage();
      }
    }
  };

  // Function to handle next page navigation
  const handleNextPage = () => {
    if (pageIndex < pageCount - 1) {
      if (props.pagination?.setPagination) {
        props.pagination.setPagination((prev: PaginationState) => ({
          ...prev,
          pageIndex: prev.pageIndex + 1,
        }));
      } else {
        typedTable.nextPage();
      }
    }
  };

  // Check if previous/next buttons should be disabled
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  return (
    <div className="flex items-center flex-col sm:flex-row gap-4 order-1 md:order-2">
      <span className="whitespace-nowrap">{paginationInfo}</span>
      <div className="pagination">
        <button
          className="btn"
          onClick={handlePreviousPage}
          disabled={!canPreviousPage}
          aria-label="previous page"
        >
          <KeenIcon
            icon="black-left"
            className="rtl:transform rtl:rotate-180"
          />
        </button>

        {renderEllipsisPrevButton()}

        <Fragment>{renderPageButtons()}</Fragment>

        {renderEllipsisNextButton()}

        <button
          className="btn"
          onClick={handleNextPage}
          disabled={!canNextPage}
          aria-label="next page"
        >
          <KeenIcon
            icon="black-right"
            className="rtl:transform rtl:rotate-180"
          />
        </button>
      </div>
    </div>
  );
};

export { DataGridPagination };
